import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, UserRound } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "En attente de validation", color: "bg-amber-100 text-amber-800" },
  validated: { label: "Validé", color: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Refusé", color: "bg-red-100 text-red-800" },
};

/** Déclare les conducteurs de l'entreprise avant leur affectation à une mission. */
export default function DriverRegistry() {
  const [drivers, setDrivers] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [license, setLicense] = useState("B");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transporter/drivers");
      setDrivers(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addDriver = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setSaving(true);
    try {
      await api.post("/transporter/drivers", {
        name: name.trim(), phone: phone.trim() || null, license_category: license,
      });
      setName(""); setPhone(""); setLicense("B");
      toast.success("Conducteur déclaré — validation documentaire requise");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const removeDriver = async (id) => {
    if (!window.confirm("Retirer ce conducteur de votre entreprise ?")) return;
    try {
      await api.delete(`/transporter/drivers/${id}`);
      setDrivers((items) => items.filter((d) => d.id !== id));
      toast.success("Conducteur retiré");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="mt-6 border border-[#1F4032]/10 bg-white rounded-md p-5" data-testid="driver-registry">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <UserRound className="w-5 h-5 text-[#1F4032] mt-0.5" />
          <div>
            <h2 className="font-display font-bold text-lg">Conducteurs de l&apos;entreprise</h2>
            <p className="text-xs text-neutral-600 mt-1">Chaque conducteur affecté doit être déclaré et validé avant le chargement. Aucun remplacement non enregistré n&apos;est autorisé.</p>
          </div>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />}
      </div>

      <form onSubmit={addDriver} className="grid md:grid-cols-[1fr_1fr_120px_auto] gap-2 items-end mb-4">
        <label className="text-xs font-medium">Nom et prénom<Input data-testid="driver-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Alex Martin" className="mt-1 h-10" required /></label>
        <label className="text-xs font-medium">Téléphone (facultatif)<Input data-testid="driver-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78" className="mt-1 h-10" /></label>
        <label className="text-xs font-medium">Permis<select data-testid="driver-license-select" value={license} onChange={(e) => setLicense(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-sm"><option>B</option><option>C1</option><option>C1E</option><option>C</option><option>CE</option></select></label>
        <Button data-testid="driver-add-button" type="submit" disabled={saving || name.trim().length < 2} className="h-10 rounded-md bg-[#1F4032] text-white gap-1"><Plus className="w-4 h-4" /> Ajouter</Button>
      </form>

      {drivers.length === 0 ? (
        <div className="text-sm text-neutral-500 border border-dashed border-neutral-200 rounded-md p-4">Aucun conducteur supplémentaire déclaré. Le titulaire KYC peut être affecté directement.</div>
      ) : (
        <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-md">
          {drivers.map((driver) => {
            const status = STATUS_LABEL[driver.status] || STATUS_LABEL.pending;
            return (
              <div key={driver.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                <div><div data-testid={`driver-name-${driver.id}`} className="font-medium text-sm">{driver.name}</div><div className="text-xs text-neutral-500">Permis {driver.license_category || "—"}{driver.phone ? ` · ${driver.phone}` : ""}</div></div>
                <div className="flex items-center gap-2"><span data-testid={`driver-status-${driver.id}`} className={`px-2 py-1 text-[11px] rounded ${status.color}`}>{status.label}</span><button data-testid={`driver-remove-${driver.id}`} type="button" onClick={() => removeDriver(driver.id)} className="p-1.5 text-neutral-500 hover:text-red-600" aria-label={`Retirer ${driver.name}`}><Trash2 className="w-4 h-4" /></button></div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
